import { DEFAULT_TIME_FRAME, TIME_FRAMES, type TimeFrame } from "@domains/engagement/shared/timeFrame.ts";
import { FEEDBACK_METRIC_IDS, FEEDBACK_SOURCES, type FeedbackMetricId, type FeedbackSource } from "@domains/feedback/shared/feedbackTypes.ts";
import { DEFAULT_REPORTING_SCOPE, REPORTING_SCOPES, type ReportingScope } from "@packages/core/organization/scope.ts";
import { parseAsString, parseAsStringEnum, useQueryState, useQueryStates } from "nuqs";
import { useMemo } from "react";
import type { FeedbackReportFilters, FeedbackReportSentiment } from "#shared/feedbackReportFilters.ts";
import { toFeedbackReportType } from "./toFeedbackReportType.ts";

export const FEEDBACK_SCOPE_QUERY_KEY = "feedback-scope";
export const FEEDBACK_PERIOD_QUERY_KEY = "feedback-period";
export const FEEDBACK_TYPE_QUERY_KEY = "feedback-type";
export const FEEDBACK_SENTIMENT_QUERY_KEY = "feedback-sentiment";
export const FEEDBACK_METRIC_QUERY_KEY = "feedback-metric";
export const FEEDBACK_LABEL_QUERY_KEY = "feedback-label";

export const FEEDBACK_FILTER_QUERY_KEYS = [FEEDBACK_TYPE_QUERY_KEY, FEEDBACK_SENTIMENT_QUERY_KEY, FEEDBACK_METRIC_QUERY_KEY, FEEDBACK_LABEL_QUERY_KEY] as const;

export const FEEDBACK_FILTER_SENTIMENTS = ["Positive", "Constructive"] as const satisfies ReadonlyArray<FeedbackReportSentiment>;
export type FeedbackFilterSentiment = (typeof FEEDBACK_FILTER_SENTIMENTS)[number];

const QUERY_STATE_OPTIONS = { history: "replace" as const, clearOnDefault: true, shallow: false };

const scopeParser = parseAsStringEnum<ReportingScope>([...REPORTING_SCOPES])
    .withDefault(DEFAULT_REPORTING_SCOPE)
    .withOptions(QUERY_STATE_OPTIONS);
const timeFrameParser = parseAsStringEnum<TimeFrame>([...TIME_FRAMES])
    .withDefault(DEFAULT_TIME_FRAME)
    .withOptions(QUERY_STATE_OPTIONS);

const feedbackFilterParsers = {
    feedbackType: parseAsStringEnum<FeedbackSource>([...FEEDBACK_SOURCES]),
    sentiment: parseAsStringEnum<FeedbackFilterSentiment>([...FEEDBACK_FILTER_SENTIMENTS]),
    metricId: parseAsStringEnum<FeedbackMetricId>([...FEEDBACK_METRIC_IDS]),
    labelId: parseAsString
};

const FEEDBACK_FILTER_URL_KEYS = {
    feedbackType: FEEDBACK_TYPE_QUERY_KEY,
    sentiment: FEEDBACK_SENTIMENT_QUERY_KEY,
    metricId: FEEDBACK_METRIC_QUERY_KEY,
    labelId: FEEDBACK_LABEL_QUERY_KEY
};

export function useFeedbackScope() {
    return useQueryState(FEEDBACK_SCOPE_QUERY_KEY, scopeParser);
}

export function useFeedbackTimeFrame() {
    return useQueryState(FEEDBACK_PERIOD_QUERY_KEY, timeFrameParser);
}

export function useFeedbackFilterState() {
    return useQueryStates(feedbackFilterParsers, { ...QUERY_STATE_OPTIONS, urlKeys: FEEDBACK_FILTER_URL_KEYS });
}

/** @public */
export function useFeedbackReportFilters(): FeedbackReportFilters {
    const [{ feedbackType, sentiment, metricId, labelId }] = useFeedbackFilterState();

    return useMemo(() => ({ type: feedbackType ? toFeedbackReportType(feedbackType) : null, sentiment, metricId, labelId }), [feedbackType, sentiment, metricId, labelId]);
}
